import { useEffect, useState } from "react";
import { useRoute } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle, XCircle, FileText, AlertTriangle } from "lucide-react";
import { PublicFormViewer } from "@/components/forms/PublicFormViewer";
import TurboClaimClient from "@/api/client";
import { fetchPdfBlob } from "@/lib/pdf-blob-fetcher";
import { getApiErrorMessage } from "@/lib/errors";
import { env } from "@/config/env";
import type { 
  GetFormByTokenResponse
} from "@/api/generated/client";
import type { FieldPlacement } from "@/types/template-fields";

// Create a public API client without authentication
const publicApiClient = new TurboClaimClient({
  basePath: env.apiBaseUrl,
  // No accessToken needed for public endpoints
});

export default function PublicFormSubmit() {
  const [, params] = useRoute("/forms/:accessToken");
  const accessToken = params?.accessToken || "";
  
  const [status, setStatus] = useState<"loading" | "ready" | "submitting" | "success" | "error" | "permanent-error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [formData, setFormData] = useState<GetFormByTokenResponse | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>("");
  const [fieldPlacements, setFieldPlacements] = useState<FieldPlacement[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [claimData, setClaimData] = useState<Record<string, unknown>>({});

  // Fetch form data
  useEffect(() => {
    if (status !== "loading") return;

    if (!accessToken) {
      setStatus("error");
      setErrorMessage("Invalid form link");
      return;
    }

    const loadFormData = async () => {
      try {
        const data = await publicApiClient.formTemplates.getFormByToken({ accessToken });
        setFormData(data);
        
        // Check if form is still valid
        if (data.document.status === "completed") {
          setStatus("permanent-error");
          setErrorMessage("This form has already been submitted.");
          return;
        }
        if (data.document.status === "cancelled") {
          setStatus("permanent-error");
          setErrorMessage("This form link was cancelled by the sender and is no longer active.");
          return;
        }
        const expiresAt = new Date(data.document.expiresAt);
        if (expiresAt < new Date()) {
          setStatus("permanent-error");
          setErrorMessage("This form has expired.");
          return;
        }

        // Set PDF URL - fetch as blob with proper cache control to avoid 304 issues
        if (data.template?.pdfTemplateUrl) {
          // Fetch PDF as blob to avoid 304 caching issues (no auth needed for public endpoints)
          try {
            const pdfBlob = await fetchPdfBlob(data.template.pdfTemplateUrl, env.apiBaseUrl);
            const blobUrl = URL.createObjectURL(pdfBlob);
            setPdfBlobUrl(blobUrl);
          } catch (pdfError) {
            console.error('Failed to fetch PDF as blob:', pdfError);
            // Fall back to direct URL if blob fetch fails
            setPdfBlobUrl(data.template.pdfTemplateUrl);
          }
        }

        // Get field placements from template response
        if (data.template?.fieldPlacements) {
          setFieldPlacements(data.template.fieldPlacements as FieldPlacement[]);
        }

        // Store claim data for pre-filling fields
        if (data.claimData) {
          setClaimData(data.claimData);
        }

        setStatus("ready");
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(error.message || "Failed to load form");
      }
    };

    loadFormData();
  }, [accessToken, status]);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const handleSubmit = async () => {
    // Validate required fields. The server treats a missing `required` flag as
    // required (`required ?? true`), so mirror that default here to avoid
    // submissions that only fail server-side.
    const requiredFields = fieldPlacements.filter(
      (f) => (f.type === 'text_input' || f.type === 'signature') && (f.required ?? true),
    );
    const missingFields = requiredFields.filter((f) => !fieldValues[f.id] || fieldValues[f.id].trim() === '');
    
    if (missingFields.length > 0) {
      setErrorMessage(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    const invalidInitials = fieldPlacements.find((field) => {
      if (!field.fieldKey.startsWith('homeowner_initials_')) return false;
      const value = fieldValues[field.id] ?? '';
      return value.trim() !== '' && !/^[A-Z]{2}$/.test(value);
    });
    if (invalidInitials) {
      setErrorMessage(`${invalidInitials.label} must contain exactly 2 letters.`);
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      // Date inputs produce ISO YYYY-MM-DD; stamp as M/D/YYYY instead.
      const formatFieldValue = (f: (typeof fieldPlacements)[number]) => {
        const value = fieldValues[f.id];
        if (f.fieldKey.startsWith('homeowner_checkbox_')) {
          return value === 'true' ? 'X' : '';
        }
        if (f.fieldKey.startsWith('homeowner_date_')) {
          const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
          if (match) return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
        }
        return value;
      };

      // Convert field values to annotations for backend compatibility
      const backendAnnotations = fieldPlacements
        .filter((f) => f.type === 'text_input' && fieldValues[f.id])
        .map((f) => ({
          text: formatFieldValue(f),
          fieldKey: f.fieldKey,
          page: f.page,
          x: f.x,
          y: f.y,
          width: f.width,
          height: f.height,
          fontSize: f.fontSize,
          fontFamily: f.fontFamily,
          textAlign: f.textAlign,
          multiline: f.multiline,
        }));

      // Get all signature fields with their placements
      const signatureFields = fieldPlacements.filter((f) => f.type === 'signature');
      const signatureData = signatureFields
        .filter((f) => fieldValues[f.id])
        .map((f) => ({
          image: fieldValues[f.id],
          page: f.page,
          x: f.x,
          y: f.y,
          width: f.width,
          height: f.height,
        }));

      // Only require a signature when the form has a *required* signature field
      // (required ?? true, matching the server). Text-only forms and forms whose
      // signature is explicitly optional can be submitted without one.
      const requiresSignature = signatureFields.some((f) => f.required ?? true);
      if (requiresSignature && signatureData.length === 0) {
        setErrorMessage("Please provide a signature");
        setStatus("ready");
        return;
      }

      await publicApiClient.formTemplates.submitForm({
        accessToken,
        submitFormDocumentRequest: {
          annotations: backendAnnotations,
          signaturePlacements: signatureData,
        },
      });

      setStatus("success");
    } catch (error) {
      // Stay on the form (not the error page) so the user doesn't lose their input.
      // getApiErrorMessage surfaces the backend's specific message (e.g. which
      // required field is missing) instead of the generic ResponseError text.
      setStatus("ready");
      setErrorMessage(await getApiErrorMessage(error));
    }
  };

  // Loading state
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading form...
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Form Submitted Successfully!
            </CardTitle>
            <CardDescription>
              Your signed document has been received. You can safely close this window.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Error state
  if (status === "error" || status === "permanent-error") {
    // Tailor the message for known terminal states so the homeowner understands
    // why the link no longer works (cancelled / expired / already submitted)
    // instead of seeing a generic "unable to load" error.
    const docStatus = formData?.document?.status;
    let title = "Unable to Load Form";
    let TitleIcon = XCircle;
    let iconColor = "text-red-600";
    if (status === "permanent-error") {
      if (docStatus === "cancelled") {
        title = "Link Cancelled";
        TitleIcon = AlertTriangle;
        iconColor = "text-amber-600";
      } else if (docStatus === "completed") {
        title = "Already Submitted";
        TitleIcon = CheckCircle;
        iconColor = "text-green-600";
      } else {
        title = "Link Expired";
        TitleIcon = AlertTriangle;
        iconColor = "text-amber-600";
      }
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${iconColor}`}>
              <TitleIcon className="h-5 w-5" />
              {title}
            </CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          {status === "error" && (
            <CardContent>
              <Button
                variant="outline"
                onClick={() => {
                  setStatus("loading");
                  setErrorMessage("");
                }}
                className="w-full"
              >
                Try Again
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    );
  }

  // Ready/Submitting state - main form
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-6 w-6 text-blue-600" />
              <div>
                <CardTitle>{formData?.template?.name || "Document"}</CardTitle>
                {formData?.template?.description && (
                  <CardDescription>{formData.template.description}</CardDescription>
                )}
              </div>
            </div>
            {formData?.document && (
              <div className="mt-4 text-sm text-slate-600">
                <p><strong>Recipient:</strong> {formData.document.recipientName}</p>
                <p><strong>Expires:</strong> {new Date(formData.document.expiresAt).toLocaleDateString()}</p>
              </div>
            )}
          </CardHeader>
        </Card>

        {pdfBlobUrl ? (
          <div className="h-[calc(100dvh-250px)] md:h-[calc(100vh-250px)]">
            <PublicFormViewer
              pdfUrl={pdfBlobUrl}
              fieldPlacements={fieldPlacements}
              fieldValues={fieldValues}
              claimData={claimData}
              onFieldChange={(fieldId, value) => {
                setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
              }}
              onSignatureChange={(fieldId, signatureDataUrl) => {
                setFieldValues((prev) => ({ ...prev, [fieldId]: signatureDataUrl }));
              }}
            />
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="flex items-center justify-center">
                <p className="text-slate-500">No document available</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Alert */}
        {errorMessage && status !== "submitting" && (
          <Alert variant="destructive" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {/* Submit Button */}
        <div className="mt-6 flex justify-center">
          <Button
            onClick={handleSubmit}
            disabled={status === "submitting"}
            className="min-w-[200px]"
            size="lg"
          >
            {status === "submitting" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Signed Document"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
